import { PagingResultModelOfMessagingLogModel, MessagingLogModel, ActionPayload } from "../../gql-types.generated";
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { SerializableError, serializeError } from '../../util/ErrorSerialization';

interface SliceState {
    error?: SerializableError;
    eventPagingResult?: PagingResultModelOfMessagingLogModel;
    eventLogList?: MessagingLogModel[];
    eventLogRequestsInFlight: number;
    updateLogsStatus?: ActionPayload;
    currentPage: number;
    pageCursors: Record<number, string | undefined>;
};

const initialState: SliceState = {
    error: undefined,
    eventPagingResult: undefined,
    eventLogList: undefined,
    eventLogRequestsInFlight: 0,
    updateLogsStatus: undefined,
    currentPage: 1,
    pageCursors: {}
};

export const slice = createSlice({
    name: 'eventLogs',
    initialState,
    reducers: {
        fetchError: (state, action: PayloadAction<SerializableError>) => {
            // eslint-disable-next-line no-param-reassign
            state.error = action.payload;
            // Clear the list so NoRowsOverlay displays the error
            // eslint-disable-next-line no-param-reassign
            state.eventLogList = undefined;
        },

        clearError: state => {
            // eslint-disable-next-line no-param-reassign
            state.error = undefined;
        },

        clearState: state => {
            // Don't clear error - let it persist until explicitly cleared with clearError()
            // eslint-disable-next-line no-param-reassign
            state.eventLogList = undefined;
            // eslint-disable-next-line no-param-reassign
            state.eventLogRequestsInFlight = 0;
            // eslint-disable-next-line no-param-reassign
            state.eventPagingResult = undefined;
            // eslint-disable-next-line no-param-reassign
            state.updateLogsStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.currentPage = 1;
            // eslint-disable-next-line no-param-reassign
            state.pageCursors = {};
        },

        clearAllCursors: (state) => {
            // eslint-disable-next-line no-param-reassign
            state.pageCursors = {};
        },

        setCurrentPage: (state, action: PayloadAction<number>) => {
            // eslint-disable-next-line no-param-reassign
            state.currentPage = action.payload;
        },

        setCursorForPage: (state, action: PayloadAction<{page: number, cursor: string | undefined}>) => {
            // eslint-disable-next-line no-param-reassign
            state.pageCursors[action.payload.page] = action.payload.cursor;
        },

        fetchEventLogListSuccess: (state, action: PayloadAction<PagingResultModelOfMessagingLogModel>) => {
            // eslint-disable-next-line no-param-reassign
            state.eventPagingResult = action.payload;
            // For server-side pagination, replace the list instead of accumulating
            // eslint-disable-next-line no-param-reassign
            state.eventLogList = action.payload.nodes as MessagingLogModel[] || [];
            
            // Cache the cursor for the next page if there is one
            // This cursor will be used to navigate FROM current page TO next page
            if (action.payload.cursor?.nextPage) {
                // eslint-disable-next-line no-param-reassign
                state.pageCursors[state.currentPage + 1] = action.payload.cursor.nextPage;
            }
        },

        incrementEventLogRequestsInFlight: state => {
            // eslint-disable-next-line no-param-reassign
            state.eventLogRequestsInFlight += 1;
        },

        decrementEventLogRequestsInFlight: state => {
            // eslint-disable-next-line no-param-reassign
            if (state.eventLogRequestsInFlight > 0) state.eventLogRequestsInFlight -= 1;
        },

        captureUpdateLogsStatus: (state, action: PayloadAction<ActionPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.updateLogsStatus = action.payload;
        },
    },

});

export const selectError = (state: RootState): SerializableError | undefined => state.eventLogs.error;
export const selectEventLogList = (state: RootState): MessagingLogModel[] | undefined => state.eventLogs.eventLogList;
export const selectRequestsInFlight = (state: RootState): number => state.eventLogs.eventLogRequestsInFlight;
export const selectEventLogPagingResult = (state: RootState): PagingResultModelOfMessagingLogModel | undefined => state.eventLogs.eventPagingResult;
export const selectUpdateLogsStatus = (state: RootState): ActionPayload | undefined => state.eventLogs.updateLogsStatus;
export const selectCurrentPage = (state: RootState): number => state.eventLogs.currentPage;
export const selectPageCursors = (state: RootState): Record<number, string | undefined> => state.eventLogs.pageCursors;

export const {
    clearError,
    fetchError,
    clearState,
    clearAllCursors,
    setCurrentPage,
    setCursorForPage,
    fetchEventLogListSuccess,
    incrementEventLogRequestsInFlight,
    decrementEventLogRequestsInFlight,
    captureUpdateLogsStatus
} = slice.actions;

export default slice.reducer;